import React, { useState, lazy } from 'react';
import { ArrowUpDownIcon, ArrowDownIcon, ArrowUpIcon, InfoIcon, TrophyIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
import { calculateConfidenceInterval } from '../utils/statistics';
import { Tooltip } from './Tooltip';
interface ImagePerformanceTableProps {
  data: ImageData[];
}
type SortField = 'stoppingPower' | 'views' | 'engagementRate';
type SortDirection = 'asc' | 'desc';
export const ImagePerformanceTable: React.FC<ImagePerformanceTableProps> = ({
  data
}) => {
  const [sortField, setSortField] = useState<SortField>('stoppingPower');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };
  const sortedData = [...data].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
  const getSortIcon = (field: SortField) => {
    if (field !== sortField) return <ArrowUpDownIcon className="h-4 w-4 opacity-50" data-id="element-194" data-mp="hk49648xfskv" />;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4" data-id="element-195" data-mp="cr1f7v08mh45" /> : <ArrowDownIcon className="h-4 w-4" data-id="element-196" data-mp="rgqvyyktctpj" />;
  };
  return <div className="bg-white rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5] overflow-hidden" data-id="element-197" data-mp="rew6r82ieffn">
      <div className="overflow-x-auto" data-id="element-198" data-mp="mk0dk4ljdm0y">
        <table className="min-w-full divide-y divide-[#F2F3F5]" data-id="element-199" data-mp="10hoytdjimv6j">
          <thead className="bg-[#F9F9FB]" data-id="element-200" data-mp="wftzgel5arve">
            <tr data-id="element-201" data-mp="wgvd8qtjpsgt">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-202" data-mp="wgbqmwsyafwg">
                Asset
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-203" data-mp="2y8zy5icmnpi1">
                <div className="flex items-center gap-1.5" data-id="element-204" data-mp="166ozpahthe7r">
                  SPR
                  <Tooltip content="Stopping Power Rating (SPR) shows how well this image grabs attention by turning impressions into actual views." data-id="element-205" data-mp="1b4gz0pksk2lz" />
                </div>
              </th>

              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[#707070] uppercase tracking-wider" data-id="element-206" data-mp="1k5yec5qdk09m">
                Audience Signals
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#F2F3F5]" data-id="element-207" data-mp="1txb50u0fske2">
            {sortedData.map((item, index) => <tr key={item.id} className={`hover:bg-[#F9F9FB] transition-colors ${index === 0 ? 'bg-[#D1005D]/5' : ''}`} data-id="element-208" data-mp="1yw5kpn33xsty" data-mpi={index}>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-209" data-mp="27vj00wdwxbml">
                  <div className="flex items-center" data-id="element-210" data-mp="3sda91ug01hkt">
                    <div className="flex items-center gap-2" data-id="element-211" data-mp="3saxhcpkfvsuk">
                      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-none bg-[#F9F9FB]" data-id="element-212" data-mp="3oanbre0k74g8">
                        <img src={item.thumbnailUrl} alt={item.name} className="h-full w-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-213" data-mp="3jd53zqya2v9t" />
                      </div>
                      <div className="ml-4" data-id="element-214" data-mp="aywfov2sgqng">
                        <div className="text-sm font-medium text-[#141617]" data-id="element-215" data-mp="64s767dvkak9">
                          {item.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-216" data-mp="yo7iei3p8v48">
                  <div className="flex items-center" data-id="element-217" data-mp="tud0nk7nmq7s">
                    <div className="w-24 bg-gray-100 rounded-none h-2.5" data-id="element-218" data-mp="edefrur38qq8">
                      <div className="h-2.5 rounded-sm bg-[#D1005D]" style={{
                    width: `${item.stoppingPower}%`
                  }} data-id="element-219" data-mp="ec3ga74oe54x" />
                    </div>
                    <span className="ml-2 text-sm font-medium text-[#141617]" data-id="element-220" data-mp="9fcy9e9b8zgv">
                      {item.stoppingPower}
                    </span>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-[#707070]" data-id="element-221" data-mp="1238tsfn58lc2">
                  {item.views.toLocaleString()}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};